import React, { useEffect } from "react";
import { motion } from "framer-motion";
import {
  FaBook,
  FaShieldAlt,
  FaCloud,
  FaCheckCircle,
  FaQuoteLeft,
  FaExternalLinkAlt,
  FaAward,
} from "react-icons/fa";
import bookCover from "../assets/images/book.jpg";
import threatIcon from "../assets/images/threat.jpg";
import architectureIcon from "../assets/images/architecture.jpg";
import complianceIcon from "../assets/images/compliance.jpg";

// Citation logos
import bookAuthorityLogo from "../assets/images/bookAuthorityLogo.png";
import cyberSkillsLogo from "../assets/images/cyberSkillsLogo.jpeg";
import tariMoeLogo from "../assets/images/tariMoeLogo.png";
import vikoLibraryLogo from "../assets/images/vikoLibraryLogo.png";
import cloudLogo from "../assets/images/cloud.png";
import SEO from "../components/common/SEO";

const BookPage = () => {
  // Canvas Animation Logic
  useEffect(() => {
    const canvas = document.getElementById("dotsCanvas");
    const ctx = canvas.getContext("2d");
    let width = (canvas.width = window.innerWidth);
    let height = (canvas.height = window.innerHeight);

    let particleCount = window.innerWidth < 640 ? 30 : 60;
    let particles = [];

    window.addEventListener("resize", () => {
      width = canvas.width = window.innerWidth;
      height = canvas.height = window.innerHeight;
      particleCount = window.innerWidth < 640 ? 30 : 60;
      particles = [];
      createParticles();
    });

    class Particle {
      constructor() {
        this.x = Math.random() * width;
        this.y = Math.random() * height;
        this.vx = Math.random() * 0.3 - 0.15;
        this.vy = Math.random() * 0.3 - 0.15;
        this.radius = window.innerWidth < 640 ? 1 : 1.5;
      }

      draw() {
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.radius, 0, Math.PI * 2);
        ctx.fillStyle = "rgba(59, 130, 246, 0.5)";
        ctx.fill();
      }

      update() {
        this.x += this.vx;
        this.y += this.vy;

        if (this.x < 0 || this.x > width) this.vx = -this.vx;
        if (this.y < 0 || this.y > height) this.vy = -this.vy;
      }
    }

    function createParticles() {
      for (let i = 0; i < particleCount; i++) {
        particles.push(new Particle());
      }
    }

    function drawLines() {
      let maxDist = 120;
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          let dist = Math.hypot(
            particles[i].x - particles[j].x,
            particles[i].y - particles[j].y,
          );
          if (dist < maxDist) {
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = `rgba(59, 130, 246, ${0.2 * (1 - dist / maxDist)})`;
            ctx.stroke();
          }
        }
      }
    }

    function animate() {
      ctx.clearRect(0, 0, width, height);
      for (let particle of particles) {
        particle.update();
        particle.draw();
      }
      drawLines();
      requestAnimationFrame(animate);
    }

    createParticles();
    animate();
  }, []);

  const highlights = [
    {
      title: "Threat Modeling",
      description:
        "Learn how to identify, analyze, and mitigate threats in cloud-native environments.",
      icon: threatIcon,
      iconComponent: <FaShieldAlt className="text-3xl text-red-400" />,
    },
    {
      title: "Secure Architecture",
      description:
        "Understand the fundamentals of building secure, scalable cloud architectures.",
      icon: architectureIcon,
      iconComponent: <FaCloud className="text-3xl text-blue-400" />,
    },
    {
      title: "Compliance Best Practices",
      description:
        "Explore regulatory compliance strategies for cloud-native environments.",
      icon: complianceIcon,
      iconComponent: <FaCheckCircle className="text-3xl text-green-400" />,
    },
  ];

  const citations = [
    {
      title: "Book Authority Awards",
      link: "https://bookauthority.org/books/best-selling-software-security-books",
      logo: bookAuthorityLogo,
    },
    {
      title: "Cloud Native Security Book (Tari Moe)",
      link: "https://tari.moe/2024/cloud-native-security-book.html",
      logo: tariMoeLogo,
    },
    {
      title: "University of Lithuania",
      link: "https://biblioteka.viko.lt/media/uploads/sites/25/2023/12/EIF-2024-m.-sausis.pdf",
      logo: vikoLibraryLogo,
    },
    {
      title: "University of Ireland",
      link: "https://cyberskills.ie/media/Module-Cloud-Security-ArchitectingDescriptor_Final.pdf",
      logo: cyberSkillsLogo,
    },
    {
      title: "Cloud Marathon Runner",
      link: "https://thecloudmarathoner.com/index.php/2023/10/08/book-review-what-you-need-to-know-about-cloud-native-software-%f0%9f%94%90-security-handbook-%e2%81%89-%f0%9f%a4%94/",
      logo: cloudLogo,
    },
  ];

  const testimonials = [
    {
      quote: "A must-read for any cloud security professional.",
      author: "Kevin Streater",
      role: "Security Architect",
    },
    {
      quote: "Highly practical and valuable for security teams.",
      author: "Gagan Sarawgi",
      role: "Cloud Engineer",
    },
    {
      quote: "An indispensable guide for navigating cloud security.",
      author: "Kaila Pollart",
      role: "DevSecOps Lead",
    },
  ];

  const bookStructuredData = {
    "@context": "https://schema.org",
    "@type": "Book",
    "name": "Cloud Native Software Security Handbook",
    "author": {
      "@type": "Person",
      "name": "Mihir Shah",
      "url": "https://mihirshah.tech"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Packt Publishing"
    },
    "datePublished": "2023",
    "description": "Comprehensive guide to cloud native security, covering threat modeling, secure architecture, and compliance best practices for modern cloud environments.",
    "genre": "Computer Science",
    "inLanguage": "en",
    "numberOfPages": "400",
    "bookFormat": "Paperback",
    "isbn": "978-1-80324-672-1",
    "url": "https://mihirshah.tech/book",
    "image": "https://mihirshah.tech/book-cover.jpg",
    "about": [
      "Cloud Security",
      "Cloud Native Security", 
      "DevSecOps",
      "Container Security",
      "Kubernetes Security",
      "Security Architecture",
      "Threat Modeling"
    ],
    "review": [
      {
        "@type": "Review",
        "reviewRating": {
          "@type": "Rating",
          "ratingValue": "5",
          "bestRating": "5"
        },
        "author": {
          "@type": "Organization",
          "name": "Book Authority"
        },
        "reviewBody": "Named one of the best selling software security books"
      }
    ],
    "offers": {
      "@type": "Offer",
      "availability": "https://schema.org/InStock",
      "price": "39.99",
      "priceCurrency": "USD",
      "seller": {
        "@type": "Organization",
        "name": "Packt Publishing"
      }
    }
  };

  return (
    <>
      <SEO
        title="Cloud Native Software Security Handbook - Mihir Shah"
        description="Comprehensive guide to cloud native security by Mihir Shah. Learn threat modeling, secure architecture, compliance, and DevSecOps best practices for modern cloud environments. Published by Packt."
        keywords="Cloud Native Software Security Handbook, Mihir Shah book, cloud security book, DevSecOps guide, container security, Kubernetes security, threat modeling, security architecture, Packt Publishing"
        url="https://mihirshah.tech/book"
        type="book"
        section="Publications"
        tags={["Book", "Cloud Security", "DevSecOps", "Security Architecture"]}
        structuredData={bookStructuredData}
        image="https://mihirshah.tech/book-cover.jpg"
      />
      <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900">
        {/* Hero Section with Canvas */}
        <section className="relative min-h-screen flex items-center justify-center">
          <canvas
            id="dotsCanvas"
            className="absolute top-0 left-0 w-full h-full"
          ></canvas>

          <div className="relative z-10 container mx-auto px-4 py-20">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center"
            >
              <motion.div
                animate={{ y: [0, -10, 0] }}
                transition={{ repeat: Infinity, duration: 3, ease: "easeInOut" }}
                className="inline-block mb-8"
              >
                <img
                  src={bookCover}
                  alt="Cloud Native Software Security Handbook"
                  className="w-64 md:w-80 mx-auto rounded-lg shadow-2xl hover:shadow-blue-500/50 transition-shadow duration-300"
                />
              </motion.div>

              <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                Cloud Native Software Security Handbook
              </h1>

              <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
                The comprehensive guide to securing cloud-native applications and
                infrastructure
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <motion.a
                  href="https://a.co/d/d8yL6wR"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-8 py-4 bg-gradient-to-r from-blue-500 to-purple-500 text-white font-medium rounded-lg hover:from-blue-600 hover:to-purple-600 transition-all duration-300 inline-flex items-center gap-2"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <FaBook />
                  Get Your Copy Now
                </motion.a>

                <motion.a
                  href="#highlights"
                  className="px-8 py-4 bg-white/10 backdrop-blur-sm border border-white/20 text-white font-medium rounded-lg hover:bg-white/20 transition-all duration-300"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Learn More
                </motion.a>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Key Sections */}
        <section id="highlights" className="py-20">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                Key Sections of the Book
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {highlights.map((highlight, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    whileHover={{ scale: 1.05 }}
                    className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 hover:bg-white/10 hover:border-white/20 transition-all duration-300"
                  >
                    <div className="w-16 h-16 bg-white/10 rounded-lg flex items-center justify-center mb-4 mx-auto">
                      {highlight.iconComponent}
                    </div>
                    <h3 className="text-xl font-semibold text-white text-center mb-3">
                      {highlight.title}
                    </h3>
                    <p className="text-gray-300 text-center">
                      {highlight.description}
                    </p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Citations & Mentions */}
        <section className="py-20 bg-gray-900/50">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                Citations & Academic References
              </h2>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {citations.map((citation, index) => (
                  <motion.a
                    key={index}
                    href={citation.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    whileHover={{ scale: 1.05 }}
                    className="group bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 hover:bg-white/10 hover:border-white/20 transition-all duration-300 flex flex-col items-center"
                  >
                    <div className="w-20 h-20 bg-white/10 rounded-lg p-2 flex items-center justify-center mb-4 group-hover:bg-white/20 transition-colors">
                      {citation.logo ? (
                        <img
                          src={citation.logo}
                          alt={citation.title}
                          className="w-full h-full object-contain"
                        />
                      ) : (
                        <FaAward className="text-3xl text-yellow-400" />
                      )}
                    </div>
                    <h3 className="text-lg font-semibold text-white text-center group-hover:text-blue-400 transition-colors">
                      {citation.title}
                    </h3>
                    <FaExternalLinkAlt className="text-gray-400 mt-2 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </motion.a>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                What Readers Are Saying
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {testimonials.map((testimonial, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    whileHover={{ scale: 1.05 }}
                    className="bg-gradient-to-br from-blue-500/10 to-purple-500/10 backdrop-blur-sm border border-white/10 rounded-xl p-6"
                  >
                    <FaQuoteLeft className="text-3xl text-blue-400 mb-4 opacity-50" />
                    <p className="text-gray-300 mb-4 italic">
                      "{testimonial.quote}"
                    </p>
                    <div className="text-right">
                      <p className="text-white font-semibold">
                        {testimonial.author}
                      </p>
                      <p className="text-gray-400 text-sm">{testimonial.role}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-20 bg-gradient-to-r from-blue-900/50 to-purple-900/50">
          <div className="container mx-auto px-4 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
                Ready to Master Cloud Security?
              </h2>
              <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
                Join thousands of security professionals who have transformed
                their approach to cloud-native security.
              </p>
              <motion.a
                href="https://a.co/d/d8yL6wR"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-8 py-4 bg-white text-gray-900 font-bold rounded-lg hover:bg-gray-100 transition-all duration-300 shadow-xl"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <FaBook />
                Get Your Copy on Amazon
                <FaExternalLinkAlt className="text-sm" />
              </motion.a>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

export default BookPage;
